import { useGetWorkspacesQuery } from '@carboncopyco/data';
import type { Workspace } from '@carboncopyco/types';
import {
  type MutableRefObject,
  type ReactNode,
  createContext,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import { SidebarContentType } from '../../types/WorkspacesTypes';
import { useUser } from './UserContext';

type ActiveWorkspace =
  | Workspace
  | {
      id: string;
    };

type WorkspaceContextType = {
  organizationId: string;
  activeWorkspace: MutableRefObject<ActiveWorkspace>;
  defaultWorkspace: Workspace | undefined;
  setActiveSidebarType: (type: SidebarContentType) => void;
  activeSidebarType: SidebarContentType;
  switchWorkspace: (
    workspace: Workspace | null,
    sideBarType?: SidebarContentType
  ) => void;
};

export const WorkspaceContext = createContext<WorkspaceContextType | null>(
  null
);

export const useWorkspaces = () => {
  const context = useContext(WorkspaceContext);

  if (context === null) {
    throw new Error(
      'useWorkspaces must be used within a WorkspaceContextProvider'
    );
  }

  return context;
};

export const WorkspaceContextProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const { activeOrganization } = useUser();
  const organizationId = activeOrganization?.id.toString() || '';
  const { data: workspaces } = useGetWorkspacesQuery(
    { organizationId },
    { skip: !organizationId }
  );
  const activeWorkspace = useRef<ActiveWorkspace>({
    id: '',
  });
  const [activeSidebarType, setActiveSidebarType] =
    useState<SidebarContentType>(SidebarContentType.Home);

  const switchWorkspace: WorkspaceContextType['switchWorkspace'] = (
    workspace,
    sideBarType?
  ) => {
    setActiveSidebarType(
      sideBarType ||
        (workspace?.workspaceType === 'playground'
          ? SidebarContentType.Draft
          : SidebarContentType.Workspace)
    );
    activeWorkspace.current = workspace || { id: '' };
  };

  const defaultWorkspace = useMemo(
    () =>
      workspaces?.find((workspace) => workspace.workspaceType === 'default'),
    [workspaces]
  );

  return (
    <WorkspaceContext.Provider
      value={{
        activeWorkspace,
        activeSidebarType,
        setActiveSidebarType,
        defaultWorkspace,
        switchWorkspace,
        organizationId,
      }}
    >
      {children}
    </WorkspaceContext.Provider>
  );
};
